import { Flex, Form, FormInstance, InputNumber, Select, Switch } from "antd";
import { DiffRow, fmtBool } from "pages/settings/policy/PolicySettingsPage";
import { PolicySection } from "pages/settings/policy/policySection";
import { SettingRow, SettingsSection } from "pages/settings/settingsRows";
import { translate } from "shared/languages";
import { CutoffUnit } from "shared/types/enum";
import { CUTOFF_UNIT_MAX, fmtCutoff } from "shared/utils/cutoff";

const KEY = "waitlist";

type Values = {
  waitlist_enabled?: boolean;
  // Unmounted while the switch is off, so absent from validated values.
  waitlist_capacity?: number;
  waitlist_auto_promote_cutoff_value?: number;
  waitlist_auto_promote_cutoff_unit?: CutoffUnit;
};

const unitLabel = (unit: CutoffUnit) =>
  translate(unit === CutoffUnit.Hour ? "class_settings.unit.hours" : "class_settings.unit.minutes");

// A switch, plus the capacity and cutoff rows while it is on. Both rules bind
// every waitlist join (client app and admin alike, through the server's
// addToWaitlistCore) and the system's promotion, so the section description
// states the scope once instead of a note per row, on or off. Switched off,
// the server removes every open spot (closeBusinessWaitlists); the capacity
// and cutoff are kept and come back with the switch. The cutoff is a whole
// number of hours or minutes (value + unit, the Product Validity control
// shape); 0 keeps the waitlist open until class start.
const Fields = ({ form }: { form: FormInstance }) => {
  const enabled = Form.useWatch("waitlist_enabled", form);
  // useWatch reads undefined while the row is unmounted; the stored unit is
  // never null for the waitlist, so "hour" only covers that moment.
  const unit: CutoffUnit = Form.useWatch("waitlist_auto_promote_cutoff_unit", form) ?? CutoffUnit.Hour;
  const toggleLabel = translate("class_settings.waitlist.toggle");
  const cutoffLabel = translate("class_settings.waitlist.auto_promote");
  // Built at render time: translate() is not reactive at module scope.
  const unitOptions = [CutoffUnit.Hour, CutoffUnit.Minute].map((value) => ({
    value,
    label: unitLabel(value),
  }));
  const cutoffMax = CUTOFF_UNIT_MAX[unit];

  return (
    <SettingsSection
      id={KEY}
      title={translate("class_settings.waitlist.section")}
      description={translate("class_settings.waitlist.subtitle")}
    >
      <SettingRow
        last={!enabled}
        title={toggleLabel}
        control={
          <Form.Item name="waitlist_enabled" valuePropName="checked" noStyle>
            <Switch aria-label={toggleLabel} />
          </Form.Item>
        }
      />
      {/* The numbers unmount while off; AntD keeps their values in the form
          store, so switching back on shows the stored rules. */}
      {enabled && (
        <>
          <SettingRow
            title={translate("class_settings.waitlist.capacity")}
            control={
              <Form.Item
                name="waitlist_capacity"
                style={{ margin: 0 }}
                rules={[{ required: true, message: translate("form.error.required") }]}
              >
                <InputNumber
                  min={1}
                  max={50}
                  precision={0}
                  style={{ width: 130 }}
                  addonAfter={translate("class_settings.unit.people")}
                  aria-label={translate("class_settings.waitlist.capacity")}
                />
              </Form.Item>
            }
          />
          <SettingRow
            last
            title={cutoffLabel}
            control={
              // Two plain side-by-side controls, never Space.Compact (see
              // ValidityStartPolicySection). The wrapper Form.Item shows the
              // value's validation message under the pair.
              <Form.Item style={{ margin: 0 }}>
                <Flex gap={8} align="center" justify="flex-end">
                  <Form.Item
                    name="waitlist_auto_promote_cutoff_value"
                    noStyle
                    rules={[
                      // An empty box is the common case (the box clamps a typed
                      // number itself); the range message is reachable only by
                      // switching the unit while holding a value over the new cap.
                      { required: true, message: translate("form.error.required") },
                      {
                        type: "integer",
                        min: 0,
                        max: cutoffMax,
                        message: translate(
                          unit === CutoffUnit.Hour
                            ? "class_settings.waitlist.cutoff_range_hour"
                            : "class_settings.waitlist.cutoff_range_minute",
                        ),
                      },
                    ]}
                  >
                    <InputNumber
                      aria-label={cutoffLabel}
                      min={0}
                      max={cutoffMax}
                      precision={0}
                      style={{ width: 90 }}
                    />
                  </Form.Item>
                  <Form.Item name="waitlist_auto_promote_cutoff_unit" noStyle>
                    <Select
                      aria-label={translate("label.unit")}
                      options={unitOptions}
                      style={{ width: 110 }}
                    />
                  </Form.Item>
                </Flex>
              </Form.Item>
            }
          />
        </>
      )}
    </SettingsSection>
  );
};

export const waitlistPolicy: PolicySection = {
  key: KEY,
  initialValues: (s) => ({
    waitlist_enabled: s.waitlist_enabled,
    waitlist_capacity: s.waitlist_capacity,
    waitlist_auto_promote_cutoff_value: s.waitlist_auto_promote_cutoff_value,
    waitlist_auto_promote_cutoff_unit: s.waitlist_auto_promote_cutoff_unit,
  }),
  buildSave: (v: Values, s) => {
    const isEnabled = v.waitlist_enabled ?? s.waitlist_enabled;
    const capacity = Number(v.waitlist_capacity ?? s.waitlist_capacity);
    const cutoffValue = Number(
      v.waitlist_auto_promote_cutoff_value ?? s.waitlist_auto_promote_cutoff_value,
    );
    const cutoffUnit = v.waitlist_auto_promote_cutoff_unit ?? s.waitlist_auto_promote_cutoff_unit;

    const rows: DiffRow[] = [];
    if (isEnabled !== s.waitlist_enabled) {
      rows.push({
        label: translate("class_settings.waitlist.toggle"),
        before: fmtBool(s.waitlist_enabled),
        after: fmtBool(isEnabled),
      });
    }
    // The numbers only matter while the waitlist is on: a change made while
    // off is neither listed nor sent, and the stored values are kept.
    if (isEnabled && capacity !== s.waitlist_capacity) {
      rows.push({
        label: translate("class_settings.waitlist.capacity"),
        before: String(s.waitlist_capacity),
        after: String(capacity),
      });
    }
    // One row whether the number, the unit or both changed.
    if (
      isEnabled &&
      (cutoffValue !== s.waitlist_auto_promote_cutoff_value ||
        cutoffUnit !== s.waitlist_auto_promote_cutoff_unit)
    ) {
      rows.push({
        label: translate("class_settings.waitlist.auto_promote"),
        before: fmtCutoff(s.waitlist_auto_promote_cutoff_value, s.waitlist_auto_promote_cutoff_unit),
        after: fmtCutoff(cutoffValue, cutoffUnit),
      });
    }

    return {
      rows,
      payload: {
        waitlist_enabled: isEnabled,
        ...(isEnabled
          ? {
              waitlist_capacity: capacity,
              waitlist_auto_promote_cutoff_value: cutoffValue,
              waitlist_auto_promote_cutoff_unit: cutoffUnit,
            }
          : {}),
      },
      // Switching it off or on needs no note (off carries the warning); the
      // note is for a change to the numbers while it stays on.
      ...(isEnabled !== s.waitlist_enabled
        ? isEnabled
          ? {}
          : { warning: translate("class_settings.waitlist.off_warning") }
        : { note: translate("class_settings.confirm.note_live") }),
    };
  },
  Fields,
};
